import React, { useEffect, useState } from 'react';
import get from 'lodash.get';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button, ContentLoader, List } from 'shared/components';
import { displayValue } from 'shared/helpers';
import { fetchLicenseManagers } from 'licenseUsers/actions';

const PAGE_SIZE = 20;

const getResults = (payload) =>
  Array.isArray(payload) ? payload : get(payload, 'results', []);

const getCount = (payload, results) =>
  Array.isArray(payload)
    ? results.length
    : get(payload, 'count', results.length);

const LicenseUserGroupManagersTab = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { groupId } = useParams();
  const [managers, setManagers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [retryToken, setRetryToken] = useState(0);

  useEffect(() => {
    setPage(0);
  }, [groupId]);

  useEffect(() => {
    if (!groupId) {
      return;
    }

    setLoading(true);
    setError(false);

    fetchLicenseManagers({
      groupId,
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
    })
      .then((res) => {
        const payload = get(res, 'data');
        const results = getResults(payload);
        setManagers(results);
        setCount(getCount(payload, results));
        setLoading(false);
      })
      .catch(() => {
        setError(true);
        setManagers([]);
        setCount(0);
        setLoading(false);
      });
  }, [groupId, page, retryToken]);

  const handleRetry = () => setRetryToken((token) => token + 1);
  const handleRowClick = (rowData) => {
    const managerId = get(rowData, 'original.id');
    if (managerId) {
      history.push(`/license_users/${managerId}`);
    }
  };

  if (loading && !managers.length) {
    return <ContentLoader text={t('Loading license managers')} />;
  }

  if (error) {
    return (
      <div className="LicenseUserGroupDetails-error">
        <p>{t('Unable to load license managers in this group')}</p>
        <Button theme="info" size="sm" onClick={handleRetry}>
          {t('Retry')}
        </Button>
      </div>
    );
  }

  const pages = Math.ceil(count / PAGE_SIZE);

  return (
    <List
      data={managers}
      noDataText={t('No license managers in this group')}
      clickable
      handleClick={handleRowClick}
      columns={[
        {
          Header: t('Email'),
          accessor: 'true_email',
          Cell: (cellData) => displayValue(cellData.value),
          minWidth: 220,
        },
        {
          Header: t('First Name'),
          accessor: 'first_name',
          Cell: (cellData) => displayValue(cellData.value),
        },
        {
          Header: t('Last Name'),
          accessor: 'last_name',
          Cell: (cellData) => displayValue(cellData.value),
        },
        {
          Header: t('Phone Number'),
          accessor: 'phone_number',
          Cell: (cellData) => displayValue(cellData.value),
          width: 180,
        },
      ]}
      page={page}
      pages={pages}
      loading={loading}
      manual
      sortable={false}
      minRows={managers.length || 5}
      showPagination={pages > 1}
      onPageChange={(newPage) => setPage(newPage)}
    />
  );
};

export default LicenseUserGroupManagersTab;
